import { useState, useEffect, memo, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Edit2, Trash2, Share2, Pencil, Camera, Tag, MoreVertical, ListPlus, Flag, Ban } from "lucide-react";
import RatingHeartDisplay from "@/components/RatingHeartDisplay";
import { Review } from "@/types";
import { toast } from "sonner";
import { mockArtists, mockUsers } from "@/data/mockData";
import { useAuth } from "@/hooks/useAuth";
import { useComments } from "@/hooks/useComments";
import { useLikes } from "@/hooks/useLikes";
import { useReviewLikeCount } from "@/hooks/useReviewLikeCounts";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";
import { formatConcertDate, getRelativeTimeAgo } from "@/utils/dateUtils";
import { getTourLink, isValidUUID } from "@/lib/linkUtils";
import { getReviewShareUrl } from "@/lib/share";
import { resolveAvatarUrl, DEFAULT_WHALE_AVATAR } from "@/lib/avatar";
// Lazy-load heavy dialog components to keep zod, react-hook-form, and react-day-picker out of the initial bundle
const CommentDialog = lazy(() => import("./CommentDialog"));
const EditReviewDialog = lazy(() => import("./EditReviewDialog"));
const AddToListDialog = lazy(() => import("./AddToListDialog"));
const ShareDialog = lazy(() => import("./ShareDialog"));
const ReportDialog = lazy(() => import("./social/ReportDialog"));
const BlockUserDialog = lazy(() => import("./social/BlockUserDialog"));
import ReviewTagsSection from "./ReviewTagsSection";
import ReviewMediaGallery from "./ReviewMediaGallery";
import ReviewMediaHero from "./media/ReviewMediaHero";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

interface ReviewCardProps {
  review: Review;
  compact?: boolean;
  /**
   * Certainty from the parent collection that the signed-in user likes this
   * review (e.g. their own Likes page). Only used until the shared likes cache
   * resolves, so the heart never flashes as unliked.
   */
  knownLiked?: boolean;
}


import { artistUrl, venueUrl } from "@/lib/entityLinks";

// Function to get artist URL - prefers slug; ArtistDetail resolves both slug and UUID.
const getArtistUrl = (artistName: string, artistId?: string): string => {
  return artistUrl({ id: artistId, name: artistName });
};

// Function to get venue URL from show_location (format: "Venue Name, City, State, Country" or "Venue Name, City, Country")
const getVenueUrl = (showLocation: string): string => {
  if (!showLocation) return '';
  
  const parts = showLocation.split(',').map(p => p.trim());
  const venueName = parts[0] || '';
  
  let stateOrCity = '';
  let country = '';
  
  if (parts.length >= 4) {
    stateOrCity = parts[2] || '';
    country = parts[3] || '';
  } else if (parts.length === 3) {
    stateOrCity = parts[1] || '';
    country = parts[2] || '';
  } else if (parts.length === 2) {
    country = parts[1] || '';
  }
  
  return venueUrl({ name: venueName, city: stateOrCity, country });
};

const ReviewCard = ({ review, compact = false, knownLiked }: ReviewCardProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toggleLike, isLiked, likesResolved } = useLikes();
  const { count: likeCount, resolved: likeCountResolved } = useReviewLikeCount(review.id);
  // Liked state comes straight from the shared like cache (optimistically
  // updated by toggleLike), so it is correct on the first render whenever the
  // metadata is already known.
  const liked = likesResolved ? isLiked('review', review.id) : !!knownLiked;
  const likeMetaUnresolved = !!user && ((!likesResolved && !knownLiked) || !likeCountResolved);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [currentReview, setCurrentReview] = useState(review);
  const [venueData, setVenueData] = useState<{venue?: string; city?: string; country?: string} | null>(null);
  // True until the concert lookup that can add the venue/date line has settled.
  const [venueLookupPending, setVenueLookupPending] = useState<boolean>(!!review.concertId);
  const [hasMedia, setHasMedia] = useState(false);
  const [fallbackBg, setFallbackBg] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [shareImage, setShareImage] = useState<string | undefined>(undefined);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const reviewShareUrl = getReviewShareUrl(currentReview.id);

  const { data: comments = [] } = useComments(currentReview.id);
  const commentCount = comments.length;
  const isOwnReview = user?.id === currentReview.userId;
  
  // Check if review was edited (updated_at is different from created_at)
  const isEdited = currentReview.createdAt && currentReview.updatedAt && 
    currentReview.createdAt !== currentReview.updatedAt;
  
  // Format concert date as "Saw on [date]" and get relative time for when review was posted
  const formattedConcertDate = currentReview.concertDate ? formatConcertDate(currentReview.concertDate) : '';
  const postedAgo = currentReview.createdAt ? getRelativeTimeAgo(currentReview.createdAt) : '';
  
  // Check if review has media
  useEffect(() => {
    const checkMedia = async () => {
      const { count } = await supabase
        .from('review_media')
        .select('*', { count: 'exact', head: true })
        .eq('review_id', review.id);
      setHasMedia((count || 0) > 0);
    };
    checkMedia();
  }, [review.id]);

  // Resolve a fallback background image (tour → artist) when the review has no media
  useEffect(() => {
    if (hasMedia) {
      setFallbackBg(null);
      return;
    }
    let active = true;
    const resolve = async () => {
      // 1) Tour image
      if (currentReview.tourId && isValidUUID(currentReview.tourId)) {
        const { data: t } = await supabase
          .from('tours')
          .select('image, artist_id')
          .eq('id', currentReview.tourId)
          .maybeSingle();
        if (!active) return;
        if (t?.image) { setFallbackBg(t.image); return; }
        // 2) Artist image via tour.artist_id
        if (t?.artist_id) {
          const { data: a } = await supabase
            .from('artists')
            .select('image')
            .eq('id', t.artist_id)
            .maybeSingle();
          if (!active) return;
          if (a?.image) { setFallbackBg(a.image); return; }
        }
      }
      // 3) Artist image by name (cache)
      if (currentReview.artistName) {
        const { data: cache } = await supabase
          .from('artist_image_cache')
          .select('image_url')
          .eq('artist_name', currentReview.artistName)
          .maybeSingle();
        if (!active) return;
        if (cache?.image_url) { setFallbackBg(cache.image_url); return; }
        const { data: ar } = await supabase
          .from('artists')
          .select('image')
          .ilike('name', currentReview.artistName)
          .limit(1)
          .maybeSingle();
        if (!active) return;
        if (ar?.image) { setFallbackBg(ar.image); return; }
      }
      setFallbackBg(null);
    };
    resolve();
    return () => { active = false; };
  }, [hasMedia, currentReview.tourId, currentReview.artistName]);
  
  // Debug ownership check
  useEffect(() => {
    if (user?.id && currentReview.userId) {
      console.log('Review ownership check:', {
        userId: user.id,
        reviewUserId: currentReview.userId,
        isOwnReview,
        artistName: currentReview.artistName
      });
    }
  }, [user?.id, currentReview.userId, isOwnReview, currentReview.artistName]);

  // Fetch venue data if concert_id exists
  useEffect(() => {
    let active = true;

    const fetchVenueData = async () => {
      if (!currentReview.concertId) {
        setVenueLookupPending(false);
        return;
      }

      setVenueLookupPending(true);
      const { data: concert } = await supabase
        .from('concerts')
        .select('venue, city, country')
        .eq('id', currentReview.concertId)
        .maybeSingle();

      if (!active) return;
      if (concert) {
        setVenueData(concert);
      }
      setVenueLookupPending(false);
    };

    fetchVenueData();
    return () => {
      active = false;
    };
  }, [currentReview.concertId]);

  // Resolve artist image for richer share previews
  useEffect(() => {
    if (!shareDialogOpen || !currentReview.artistName) return;

    let active = true;

    const fetchArtistImage = async () => {
      // Priority: cached artist image -> artists table image. Never the
      // reviewer's profile photo (ShareDialog falls back to a branded card).
      const { data } = await supabase
        .from('artist_image_cache')
        .select('image_url')
        .eq('artist_name', currentReview.artistName)
        .maybeSingle();

      if (!active) return;
      if (data?.image_url) {
        setShareImage(data.image_url);
        return;
      }

      const { data: artistRow } = await supabase
        .from('artists')
        .select('image')
        .ilike('name', currentReview.artistName)
        .maybeSingle();
      if (!active) return;
      setShareImage(artistRow?.image || undefined);
    };

    void fetchArtistImage();

    return () => {
      active = false;
    };
  }, [shareDialogOpen, currentReview.artistName, currentReview.userImage]);

  const [likePending, setLikePending] = useState(false);

const handleLike = async (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();

  if (!user) {
    toast.error("Please sign in to like reviews");
    return;
  }
  if (likePending) return;

  // toggleLike updates the shared like cache (and the cached count) optimistically,
  // and rolls both back on failure, so the heart flips instantly here.
  setLikePending(true);
  try {
    await toggleLike('review', review.id);
  } finally {
    setLikePending(false);
  }
};

  const handleComment = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error("Please sign in to comment");
      return;
    }
    
    setCommentDialogOpen(true);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditDialogOpen(true);
  };

  const handleEditSuccess = async () => {
    // Refetch review data
    const { data } = await supabase
      .from('reviews')
      .select(`
        *,
        profiles!inner(username, name, profile_picture)
      `)
      .eq('id', currentReview.id)
      .single();

    if (data) {
      const reviewData = data as any;
      setCurrentReview({
        id: data.id,
        userId: data.user_id,
        userName: data.profiles.name,
        userUsername: data.profiles.username,
        userImage: (data.profiles.profile_picture && data.profiles.profile_picture.trim()) || '',
        concertId: data.concert_id,
        artistName: data.artist_name,
        rating: data.rating,
        content: data.content,
        likes: data.likes || 0,
        date: data.created_at.split('T')[0],
        concertDate: data.concert_date,
        tourLeg: data.tour_leg,
        tourId: data.tour_id,
        tourName: data.tour_name,
        venue: reviewData.show_location || undefined,
        venueVerified: reviewData.venue_verified,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      });
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', currentReview.id);

      if (error) throw error;

      toast.success('Review deleted successfully');
      setDeleteDialogOpen(false);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  const handleCardClick = () => {
    if (commentDialogOpen || editDialogOpen || shareDialogOpen || deleteDialogOpen) return;
    navigate(`/reviews/${currentReview.id}`);
  };

  if (compact) {
    return (
      <article className="group relative cursor-pointer border-b border-border/40 py-3.5 transition-colors hover:bg-muted/20 overflow-hidden" onClick={handleCardClick}>
        {fallbackBg && (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.08]"
              style={{ backgroundImage: `url(${fallbackBg})` }}
            />
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/60" />
          </>
        )}
        <div className="relative flex gap-3">
          <div onClick={(e) => e.stopPropagation()}>
            <Link to={`/profile/${review.userUsername}`}>
              <Avatar className="h-9 w-9">
                <AvatarImage src={resolveAvatarUrl({ profile_picture: review.userImage, name: review.userName, username: review.userUsername }, null, 'feed-card')} alt={review.userName} loading="lazy" />
                <AvatarFallback>{(review.userName || 'U').charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>

            </Link>
          </div>
          <div className="flex-1 min-w-0">
            <div onClick={(e) => e.stopPropagation()} className="flex items-baseline gap-2 flex-wrap">
              <Link to={`/profile/${currentReview.userUsername}`} className="font-semibold text-sm hover:underline">
                {currentReview.userName}
              </Link>
              <span className="text-xs text-muted-foreground">on</span>
              <Link to={getArtistUrl(currentReview.artistName, currentReview.artistId)} className="font-semibold text-sm hover:text-primary hover:underline">
                {currentReview.artistName}
              </Link>
              {postedAgo && (
                <span className="text-xs text-muted-foreground/70 ml-auto">{postedAgo}</span>
              )}
            </div>

            <div className="mt-0.5 flex items-center gap-1.5">
              <RatingHeartDisplay
                rating={currentReview.rating}
                liked={currentReview.liked}
                size={12}
              />
              {isEdited && <Pencil className="h-3 w-3 text-muted-foreground/60 ml-1" />}
            </div>

            <p className="text-sm text-foreground/90 mt-1.5 break-words whitespace-pre-wrap leading-relaxed">{currentReview.content}</p>

            <ReviewMediaGallery reviewId={currentReview.id} compact />

            {(formattedConcertDate || venueData?.venue || currentReview.venue || currentReview.tourName) ? (
              <p onClick={(e) => e.stopPropagation()} className="text-xs text-muted-foreground/70 mt-1.5 truncate">
                {[
                  formattedConcertDate,
                  venueData?.venue || currentReview.venue,
                  currentReview.tourName,
                ].filter(Boolean).join(' · ')}
              </p>
            ) : venueLookupPending ? (
              // Reserve exactly the eventual venue/date line while the concert
              // lookup that can produce it is still in flight.
              <p aria-hidden className="text-xs mt-1.5 leading-normal">
                <span className="inline-block h-[0.8em] w-28 align-middle rounded bg-muted animate-pulse" />
              </p>
            ) : null}

            <div className="mt-2 flex items-center gap-3 text-muted-foreground">
              {user && (
                <button onClick={handleLike} disabled={likePending || likeMetaUnresolved} aria-busy={likePending} className={`inline-flex items-center gap-1 text-xs transition-colors disabled:opacity-70 ${liked ? 'text-primary' : 'hover:text-foreground'} ${likeMetaUnresolved ? 'opacity-40' : ''}`}>
                  <Heart size={14} className={liked ? 'fill-current' : ''} />
                  {likeCountResolved
                    ? (likeCount ?? 0) > 0 && <span>{likeCount}</span>
                    : <span aria-hidden className="inline-block w-[7px]" />}
                </button>
              )}
              {user && (
                <button onClick={handleComment} className="inline-flex items-center gap-1 text-xs hover:text-foreground transition-colors">
                  <MessageCircle size={14} />
                  {commentCount > 0 && <span>{commentCount}</span>}
                </button>
              )}
              <button aria-label="Share review" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShareDialogOpen(true); }} className="inline-flex items-center text-xs hover:text-foreground transition-colors">
                <Share2 size={14} />
              </button>
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    <button aria-label="More options" className="inline-flex items-center text-xs hover:text-foreground transition-colors ml-auto">
                      <MoreVertical size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAddToListOpen(true); }}>
                      <ListPlus className="mr-2 h-4 w-4" /> Add to List
                    </DropdownMenuItem>
                    {isOwnReview && (
                      <>
                        <DropdownMenuItem onClick={handleEdit}>
                          <Edit2 className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTags((prev) => !prev); }}>
                          <Tag className="mr-2 h-4 w-4" /> {showTags ? 'Hide Tags' : 'Tags'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </>
                    )}
                    {!isOwnReview && (
                      <>
                        <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReportOpen(true); }}>
                          <Flag className="mr-2 h-4 w-4" /> Report review
                        </DropdownMenuItem>
                        {currentReview.userId && (
                          <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBlockOpen(true); }} className="text-destructive focus:text-destructive">
                            <Ban className="mr-2 h-4 w-4" /> Block user
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {showTags && isOwnReview && currentReview.userId && (
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <ReviewTagsSection reviewId={currentReview.id} reviewUserId={currentReview.userId} />
              </div>
            )}
          </div>
        </div>

        <Suspense fallback={null}>
          {commentDialogOpen && (
            <CommentDialog
              open={commentDialogOpen}
              onOpenChange={setCommentDialogOpen}
              reviewId={currentReview.id}
              artistName={currentReview.artistName}
            />
          )}
          {editDialogOpen && (
            <EditReviewDialog
              open={editDialogOpen}
              onOpenChange={setEditDialogOpen}
              reviewId={currentReview.id}
              initialRating={currentReview.rating}
              initialContent={currentReview.content}
              initialLiked={currentReview.liked}
              onSuccess={handleEditSuccess}
            />
          )}
          {addToListOpen && (
            <AddToListDialog
              open={addToListOpen}
              onOpenChange={setAddToListOpen}
              itemId={currentReview.id}
              itemName={`${currentReview.artistName} - ${currentReview.userName}`}
              itemType="review"
            />
          )}
          {shareDialogOpen && (
            <ShareDialog
              open={shareDialogOpen}
              onOpenChange={setShareDialogOpen}
              title={`${currentReview.userName}'s review of ${currentReview.artistName}`}
              url={reviewShareUrl}
              image={shareImage}
              subtitle={`★ ${currentReview.rating} · ${currentReview.artistName}`}
            />
          )}
          {reportOpen && (
            <ReportDialog
              open={reportOpen}
              onOpenChange={setReportOpen}
              targetType="review"
              targetId={currentReview.id}
              targetUserId={currentReview.userId}
            />
          )}
          {blockOpen && currentReview.userId && (
            <BlockUserDialog
              open={blockOpen}
              onOpenChange={setBlockOpen}
              userId={currentReview.userId}
              displayName={currentReview.userName}
            />
          )}
        </Suspense>
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete review?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this review? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { void confirmDelete(e); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </article>
    );
  }

  // Non-compact view
  return (
    <article className="group relative cursor-pointer border-b border-border/40 py-5 md:py-6 transition-colors hover:bg-muted/30 overflow-hidden" onClick={handleCardClick}>
      {fallbackBg && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.10]"
            style={{ backgroundImage: `url(${fallbackBg})` }}
          />
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/55" />
        </>
      )}
      <div className="relative flex items-start gap-3 md:gap-4 min-w-0">
        <div onClick={(e) => e.stopPropagation()}>
          <Link to={`/profile/${review.userUsername}`}>
            <Avatar className="h-10 w-10">
              <AvatarImage src={resolveAvatarUrl({ profile_picture: review.userImage, name: review.userName, username: review.userUsername }, null, 'review-author')} alt={review.userName} loading="lazy" />
              <AvatarFallback>{(review.userName || 'U').charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>

          </Link>
        </div>
        <div className="flex-1 min-w-0">
          {/* Primary line: user on artist */}
          <div onClick={(e) => e.stopPropagation()} className="flex items-baseline gap-2 flex-wrap">
            <Link to={`/profile/${currentReview.userUsername}`} className="font-semibold hover:underline">
              {currentReview.userName}
            </Link>
            <span className="text-sm text-muted-foreground">on</span>
            <Link
              to={getArtistUrl(currentReview.artistName, currentReview.artistId)}
              className="font-bold text-base md:text-lg hover:text-primary hover:underline"
            >
              {currentReview.artistName}
            </Link>
            {postedAgo && (
              <span className="ml-auto text-xs text-muted-foreground/70">{postedAgo}</span>
            )}
          </div>

          {/* Rating row */}
          <div onClick={(e) => e.stopPropagation()} className="mt-1 flex items-center gap-1.5">
            <RatingHeartDisplay
              rating={currentReview.rating}
              liked={currentReview.liked}
              size={14}
            />
            {isEdited && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground/70">
                <Pencil className="h-3 w-3" /> edited
              </span>
            )}
          </div>

          {/* Review body */}
          <p className="text-[15px] md:text-base text-foreground/90 mt-3 break-words whitespace-pre-wrap leading-relaxed">
            {currentReview.content}
          </p>

          {/* Media — elevated hero preview when present */}
          <ReviewMediaHero reviewId={currentReview.id} />

          {/* Quiet metadata footer */}
          {(formattedConcertDate || venueData?.venue || currentReview.venue || currentReview.tourName || venueData?.city || currentReview.city) && (
            <p onClick={(e) => e.stopPropagation()} className="text-xs text-muted-foreground/70 mt-3 truncate">
              {[
                formattedConcertDate,
                currentReview.tourName && (
                  <Link
                    key="tour"
                    to={getTourLink(currentReview.tourId, currentReview.tourName)}
                    className="hover:text-foreground hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {currentReview.tourName}
                  </Link>
                ),
                (venueData?.venue || currentReview.venue) && (
                  currentReview.venue ? (
                    <Link
                      key="venue"
                      to={getVenueUrl(currentReview.venue)}
                      className="hover:text-foreground hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {venueData?.venue || currentReview.venue}
                    </Link>
                  ) : (
                    <span key="venue">{venueData?.venue}</span>
                  )
                ),
                (venueData?.city || currentReview.city) || (venueData?.country || currentReview.country)
                  ? `${venueData?.city || currentReview.city || ''}${((venueData?.city || currentReview.city) && (venueData?.country || currentReview.country)) ? ', ' : ''}${venueData?.country || currentReview.country || ''}`
                  : null,
              ]
                .filter(Boolean)
                .map((item, i, arr) => (
                  <span key={i}>
                    {item}
                    {i < arr.length - 1 && <span className="mx-1.5">·</span>}
                  </span>
                ))}
            </p>
          )}

          {/* Actions */}
          <div className="mt-3 flex items-center gap-4 text-muted-foreground">
            {user && (
              <button
                onClick={handleLike}
                disabled={likePending || likeMetaUnresolved}
                aria-busy={likePending}
                className={`inline-flex items-center gap-1.5 text-xs transition-colors disabled:opacity-70 ${liked ? 'text-primary' : 'hover:text-foreground'} ${likeMetaUnresolved ? 'opacity-40' : ''}`}
              >
                <Heart size={15} className={liked ? 'fill-current' : ''} />
                {likeCountResolved
                  ? (likeCount ?? 0) > 0 && <span>{likeCount}</span>
                  : <span aria-hidden className="inline-block w-[7px]" />}
              </button>
            )}
            {user && (
              <button onClick={handleComment} className="inline-flex items-center gap-1.5 text-xs hover:text-foreground transition-colors">
                <MessageCircle size={15} />
                {commentCount > 0 && <span>{commentCount}</span>}
              </button>
            )}
            <button aria-label="Share review" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShareDialogOpen(true); }} className="inline-flex items-center text-xs hover:text-foreground transition-colors">
              <Share2 size={15} />
            </button>
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                  <button aria-label="More options" className="inline-flex items-center text-xs hover:text-foreground transition-colors ml-auto">
                    <MoreVertical size={15} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAddToListOpen(true); }}>
                    <ListPlus className="mr-2 h-4 w-4" /> Add to List
                  </DropdownMenuItem>
                  {isOwnReview && (
                    <>
                      <DropdownMenuItem onClick={handleEdit}>
                        <Edit2 className="mr-2 h-4 w-4" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTags((prev) => !prev); }}>
                        <Tag className="mr-2 h-4 w-4" /> {showTags ? 'Hide Tags' : 'Tags'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDelete} className="text-destructive focus:text-destructive">
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </>
                  )}
                  {!isOwnReview && (
                    <>
                      <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReportOpen(true); }}>
                        <Flag className="mr-2 h-4 w-4" /> Report review
                      </DropdownMenuItem>
                      {currentReview.userId && (
                        <DropdownMenuItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBlockOpen(true); }} className="text-destructive focus:text-destructive">
                          <Ban className="mr-2 h-4 w-4" /> Block user
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {showTags && isOwnReview && currentReview.userId && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              <ReviewTagsSection reviewId={currentReview.id} reviewUserId={currentReview.userId} />
            </div>
          )}
        </div>
      </div>


      <Suspense fallback={null}>
        {commentDialogOpen && (
          <CommentDialog
            open={commentDialogOpen}
            onOpenChange={setCommentDialogOpen}
            reviewId={currentReview.id}
            artistName={currentReview.artistName}
          />
        )}
        {editDialogOpen && (
          <EditReviewDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            reviewId={currentReview.id}
            initialRating={currentReview.rating}
            initialContent={currentReview.content}
              initialLiked={currentReview.liked}
            onSuccess={handleEditSuccess}
          />
        )}
        {addToListOpen && (
          <AddToListDialog
            open={addToListOpen}
            onOpenChange={setAddToListOpen}
            itemId={currentReview.id}
            itemName={`${currentReview.artistName} - ${currentReview.userName}`}
            itemType="review"
          />
        )}
        {shareDialogOpen && (
          <ShareDialog
            open={shareDialogOpen}
            onOpenChange={setShareDialogOpen}
            title={`${currentReview.userName}'s review of ${currentReview.artistName}`}
            url={reviewShareUrl}
            image={shareImage}
            subtitle={`★ ${currentReview.rating} · ${currentReview.artistName}`}
          />
        )}
        {reportOpen && (
          <ReportDialog
            open={reportOpen}
            onOpenChange={setReportOpen}
            targetType="review"
            targetId={currentReview.id}
            targetUserId={currentReview.userId}
          />
        )}
        {blockOpen && currentReview.userId && (
          <BlockUserDialog
            open={blockOpen}
            onOpenChange={setBlockOpen}
            userId={currentReview.userId}
            displayName={currentReview.userName}
          />
        )}
      </Suspense>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete review?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this review? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { void confirmDelete(e); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  );
};

export default memo(ReviewCard);
